import { Injectable } from '@angular/core';
import { Observable, tap, of, shareReplay } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Expense } from '../../_models/Expense';
import { ExpenseType } from '../../_models/ExpenseType';
import { API_V3_URL } from 'on-common/constants';
import { IApiActionResult, IApiResult } from 'on-common/_models/ApiResult';
import { TranslateService } from '@ngx-translate/core';

const EXPENSE_URL = API_V3_URL + '/expense';

@Injectable({ providedIn: 'root' })
export class ExpenseService {
    private expenseTypesCache: IApiResult<ExpenseType[]> | null = null;
    private expenseTypesRequest$: Observable<IApiResult<ExpenseType[]>> | null = null;

    constructor(
        private http: HttpClient,
        private translate: TranslateService,
    ) {}

    GetExpensesOfCurrentUser(): Observable<IApiResult<Expense[]>> {
        return this.http.get<IApiResult<Expense[]>>(
            EXPENSE_URL + '/currentuser',
        );
    }

    GetExpenseDetails(id: number): Observable<IApiResult<Expense>> {
        return this.http.get<IApiResult<Expense>>(EXPENSE_URL + '/' + id);
    }

    RemoveExpenses(ids: number[]): Observable<IApiActionResult> {
        return this.http.post<IApiActionResult>(EXPENSE_URL + '/delete', ids);
    }

    CreateManualExpense(
        expenseToCreate: Expense,
        tempImageFileName: string,
    ): Observable<IApiResult<Expense>> {
        return this.http.post<IApiResult<Expense>>(EXPENSE_URL + '/create', {
            ExpenseToCreate: expenseToCreate,
            TempImageName: tempImageFileName,
        });
    }

    EditExpense(expenseToEdit: Expense): Observable<IApiResult<Expense>> {
        return this.http.post<IApiResult<Expense>>(
            EXPENSE_URL + '/edit',
            expenseToEdit,
        );
    }

    GetExpenseTypes(forceReload = false): Observable<IApiResult<ExpenseType[]>> {
        // Return cached data if available and forceReload is false
        if (!forceReload && this.expenseTypesCache) {
            return of(this.expenseTypesCache);
        }

        // Return ongoing request if available
        if (!forceReload && this.expenseTypesRequest$) {
            return this.expenseTypesRequest$;
        }

        // Create new request and share it
        this.expenseTypesRequest$ = this.http
            .get<IApiResult<ExpenseType[]>>(EXPENSE_URL + '/types')
            .pipe(
                tap((response) => {
                    // Compute the translations for the expense types
                    for (const expenseType of response.Result) {
                        expenseType.Name =
                            expenseType.CustomName ??
                            this.translate.instant(
                                'db.expense-types.' + expenseType.Code,
                            );
                        expenseType.CategoryName = this.translate.instant(
                            'db.expense-categories.' + expenseType.CategoryCode,
                        );
                    }
                    // Store in cache
                    this.expenseTypesCache = response;
                    // Clear the ongoing request reference
                    this.expenseTypesRequest$ = null;
                }),
                shareReplay(1)
            );

        return this.expenseTypesRequest$;
    }

    ValidExpense(expenseToValid: Expense): Observable<IApiResult<Expense>> {
        return this.http.post<IApiResult<Expense>>(EXPENSE_URL + '/validate', {
            ExpenseId: expenseToValid.Id,
            AmountToBeReimbursed: expenseToValid.AmountToBeReimbursed,
        });
    }

    RejectExpense(expenseToReject: Expense): Observable<IApiResult<Expense>> {
        return this.http.post<IApiResult<Expense>>(EXPENSE_URL + '/refuse', {
            ExpenseId: expenseToReject.Id,
            Comment: expenseToReject.ValidationComment,
        });
    }

    DuplicateExpense(expenseId: number): Observable<IApiResult<Expense>> {
        return this.http.post<IApiResult<Expense>>(
            EXPENSE_URL + '/duplicate/' + expenseId,
            null,
        );
    }

    GetTotalMileageExpense(expense: Expense): Observable<IApiResult<number>> {
        return this.http.post<IApiResult<number>>(
            EXPENSE_URL + '/mileage/total',
            expense,
        );
    }
}
